import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  CHECKOUT_CONFIG,
  CheckoutConfig,
  CheckoutRequest,
  CheckoutResponse,
  ValidateDirectDebitPayload,
  ValidateDirectDebitResponse,
} from '@checkout/util-models';
import { FORM_CONFIG, FormConfig } from '@domgen/dgx-fe-forms/shared';
import { Observable, from, of } from 'rxjs';
import { catchError, map, mergeMap, toArray } from 'rxjs/operators';
import {
  formatCheckoutCompletedItems,
  unifyCheckoutResponse,
} from '../../utils/checkout/checkout.utils';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  constructor(
    @Inject(CHECKOUT_CONFIG) protected readonly config: CheckoutConfig,
    @Inject(FORM_CONFIG) protected readonly formConfig: FormConfig,
    protected readonly httpClient: HttpClient
  ) {}

  checkoutMany(...requests: CheckoutRequest[]): Observable<CheckoutResponse> {
    return from(requests).pipe(
      mergeMap((request: CheckoutRequest) => this.checkout(request)),
      toArray(),
      map((responses: CheckoutResponse[]) => unifyCheckoutResponse(responses))
    );
  }

  checkout(payload: CheckoutRequest): Observable<CheckoutResponse> {
    const version = this.formConfig.clpEnabled ? 'v2' : 'v1';

    return this.httpClient
      .post<{ result: CheckoutResponse }>(
        `${this.config.basketApi}/${version}/checkout`,
        payload
      )
      .pipe(
        map((response: { result: CheckoutResponse }) => ({
          ...response.result,
          completedItems: formatCheckoutCompletedItems(
            payload,
            response.result
          ),
        }))
      );
  }

  validateAccountNumberSortCode({
    accountNumber,
    sortCode,
    iban,
    paymentToken,
  }: {
    accountNumber?: number | string | null;
    sortCode?: number | string | null;
    iban?: string | null;
    paymentToken?: string | null;
  }): Observable<boolean> {
    if (paymentToken) {
      return of(true);
    }

    const payload: ValidateDirectDebitPayload = {
      accountNumber: accountNumber?.toString(),
      branchCode: sortCode?.toString(),
      iban: iban?.toUpperCase(),
    };

    return this.httpClient
      .post<ValidateDirectDebitResponse>(
        `${this.config.commonApi}/v2/direct-debit/validate`,
        payload
      )
      .pipe(
        map((response: ValidateDirectDebitResponse) => response.result.isValid),
        catchError(() => of(false))
      );
  }
}
